/** @jsxRuntime classic */
/** @jsx jsx */

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { css, jsx } from '@emotion/react';
import { Colors, Typography, VisuallyHidden } from '@zapier/design-system';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import { CTAType, SectionName, useAvroTracking } from '../../../observability';
import {
  BREAKPOINTS,
  COLOR_KEYS,
  MEDIA_MAX_WIDTH,
  MEDIA_MIN_WIDTH,
} from '../../../constants';
import { CustomButton } from '../../CustomButton';
import { LottieAnimation } from '../../LottieAnimation';
import { RichTextHtml } from '../../RichTextHtml/RichTextHtml';
import { RichTextHTMLString } from '../types';
import { AnimatedHeading } from './AnimatedHeading';
import { Video } from '../../../components/Video/Video';
import { GoogleSSOButton } from '../../../components/GoogleSSOButton/GoogleSSOButton';

export interface SplitColorHeroProps {
  animationAssetsUrl?: string;
  /**
   * animationDataUrl can be a Lottie json or an mp4 file.
   */
  animationDataUrl?: string;
  animationShouldLoop?: boolean;
  animationSpeed?: number;
  backgroundColor:
    | typeof COLOR_KEYS.moss
    | typeof COLOR_KEYS.neutral800
    | typeof COLOR_KEYS.night
    | typeof COLOR_KEYS.zapier;
  ctaText: string;
  ctaUrl: string;
  description: RichTextHTMLString;
  eyebrow?: string;
  hasGoogleSSO?: boolean;
  headingGraphic?: { url: string; hiddenText: string };
  headingRotatingText?: { text: string }[];
  headingStaticText?: string;
  googleSSOText?: string;
  imageUrl?: string;
  isReversedOnMobile?: boolean;
  mediaAltText: string;
  mediaLinkText?: string;
  mediaLinkUrl?: string;
  secondaryCtaText: string;
  secondaryCtaUrl: string;
  calloutText?: RichTextHTMLString;
}

export const sharedHeadingStyles = css({
  ...Typography.pageHeader7,
  color: Colors.earth,

  [MEDIA_MIN_WIDTH.medium]: {
    ...Typography.pageHeader5,
  },

  [MEDIA_MIN_WIDTH.large]: {
    ...Typography.pageHeader3,
  },
});

const Styles = labelStyles('SplitColorHero', {
  root: (isReversedOnMobile?: boolean) => [
    css({
      display: 'flex',
      flexDirection: 'column-reverse',
      maxWidth: '2048px',
      margin: '0 auto',

      [MEDIA_MIN_WIDTH.small]: {
        display: 'grid',
        gridTemplateColumns: '45% 55%',
      },

      [MEDIA_MIN_WIDTH.extraLarge]: {
        minHeight: 'calc(100vh - 72px)',
      },
    }),
    isReversedOnMobile &&
      withModifier(
        'isReversedOnMobile',
        css({
          flexDirection: 'column',
        }),
      ),
  ],

  contentContainer: css({
    backgroundColor: Colors.neutral100,
    padding: '24px 20px',

    [MEDIA_MIN_WIDTH.small]: {
      padding: '48px 20px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      padding:
        '96px 0 96px max(calc((min(2048px, 100vw) - 1170px) / 2), 20px);',
    },
  }),

  content: css({
    height: '100%',

    [MEDIA_MIN_WIDTH.small]: {
      maxWidth: '320px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },

    [MEDIA_MIN_WIDTH.large]: {
      maxWidth: '370px',
    },
  }),

  eyebrow: css({
    ...Typography.heading,
    color: Colors.earth,
    fontWeight: 600,
    fontSize: '14px',
    marginBottom: '12px',

    [MEDIA_MIN_WIDTH.medium]: {
      fontSize: '16px',
      marginBottom: '16px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      fontSize: '18px',
      marginBottom: '20px',
    },
  }),

  headingContainer: css({
    marginBottom: '16px',

    [MEDIA_MIN_WIDTH.medium]: {
      marginBottom: '40px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      marginBottom: '80px',
    },
  }),

  heading: sharedHeadingStyles,

  headingGraphic: css({
    display: 'block',
    maxWidth: '80%',

    [MEDIA_MIN_WIDTH.large]: {
      maxWidth: '100%',
    },
  }),

  description: css({
    ...Typography.paragraph3,
    ...Typography.heading,
    color: Colors.neutral800,
    marginBottom: '24px',

    [MEDIA_MIN_WIDTH.large]: {
      ...Typography.subHeader3,
    },
  }),
  calloutText: css({
    ...Typography.smallPrint1,
  }),
  mediaContainer: (
    backgroundColor: SplitColorHeroProps['backgroundColor'],
    hasMediaLink: boolean,
    hasVideo: boolean,
  ) =>
    css({
      backgroundColor: Colors[backgroundColor],
      padding: '24px',
      position: 'relative',

      [MEDIA_MIN_WIDTH.small]: {
        padding: hasMediaLink ? '24px 24px 58px' : '24px',
      },

      [MEDIA_MIN_WIDTH.large]: {
        padding: hasMediaLink ? `80px 80px ${hasVideo ? 80 : 114}px` : '80px',
      },

      [MEDIA_MIN_WIDTH.extraLarge]: {
        padding: hasMediaLink ? `96px 96px ${hasVideo ? 96 : 130}px` : '96px',
      },
    }),

  media: css({
    width: '100%',
    '& video': {
      paddingBottom: '8px',
      [MEDIA_MAX_WIDTH.small]: {
        height: 'auto',
      },
    },

    [MEDIA_MIN_WIDTH.small]: {
      height: '100%',
      minHeight: '100%',
      maxHeight: 'calc(100vh - 72px - 192px)',
      display: 'grid',
      placeItems: 'center',
      gridTemplateRows: 'minmax(0, 1fr)',
    },

    '& > svg, & > img': {
      display: 'block',
      maxHeight: '100%',
      maxWidth: '100%',
    },
  }),

  mediaLink: (hasVideo: boolean) =>
    css({
      ...Typography.smallPrint1Bold,
      color: Colors.neutral800,
      maxWidth: '75%',
      margin: `0 ${hasVideo ? 0 : '2vw'} 0 0`,
      display: 'inline-flex',
      alignItems: 'flex-start',
      float: 'right',
      textAlign: 'right',
      textDecoration: 'none',

      [MEDIA_MIN_WIDTH.small]: {
        ...Typography.smallPrint3Bold,
        maxWidth: 'none',
        marginRight: hasVideo ? 'auto' : '1vw',
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        float: 'none',
      },
      [MEDIA_MIN_WIDTH.medium]: {
        ...Typography.smallPrint1Bold,
      },

      [MEDIA_MIN_WIDTH.large]: {
        ...Typography.paragraph3Bold,
      },
      '& > svg': {
        width: '10px',
        height: '10px',
        marginTop: '2px',
        marginLeft: '8px',
        flexShrink: 0,

        [MEDIA_MIN_WIDTH.large]: {
          width: '14px',
          height: '14px',
          marginTop: 0,
        },
      },
    }),

  ctaContainer: css({
    display: 'grid',
    gap: '24px',
  }),
});

function Heading(
  props: Pick<
    SplitColorHeroProps,
    'headingGraphic' | 'headingRotatingText' | 'headingStaticText'
  >,
) {
  if (
    props.headingRotatingText &&
    props.headingRotatingText.length > 0 &&
    props.headingStaticText
  ) {
    return (
      <AnimatedHeading
        rotatingText={props.headingRotatingText}
        staticText={props.headingStaticText}
      />
    );
  }

  if (props.headingGraphic?.url && props.headingGraphic.hiddenText) {
    return (
      <Fragment>
        <VisuallyHidden>
          <h1>{props.headingGraphic.hiddenText}</h1>
        </VisuallyHidden>
        <img
          css={Styles.headingGraphic}
          src={props.headingGraphic.url}
          alt={props.headingGraphic.hiddenText}
          role="presentation"
        />
      </Fragment>
    );
  }

  return <h1 css={Styles.heading}>{props.headingStaticText}</h1>;
}

export function SplitColorHero(props: SplitColorHeroProps) {
  const { emitCTAClickEvent } = useAvroTracking();
  const mediaContainerRef = useRef<HTMLDivElement>(null);
  const [mediaLinkStyles, setMediaLinkStyles] = useState<{
    right: number;
    bottom: number;
    visibility: 'hidden' | 'visible';
  }>({
    right: 0,
    bottom: 0,
    visibility: 'hidden',
  });

  const hasVideo =
    !!props.animationDataUrl && !props.animationDataUrl?.endsWith('.json');

  const calculateMediaLinkStyles = useCallback(() => {
    if (
      !props.mediaLinkText ||
      !props.mediaLinkUrl ||
      !mediaContainerRef.current
    ) {
      return;
    }

    if (window.innerWidth < BREAKPOINTS.small) {
      setMediaLinkStyles({ right: 0, bottom: 0, visibility: 'visible' });
      return;
    }

    const mediaElement =
      mediaContainerRef.current.querySelector('img') ||
      mediaContainerRef.current.querySelector('.lottie-animation > svg') ||
      mediaContainerRef.current.querySelector('video');

    if (!mediaElement) {
      return;
    }

    const { width: mediaWidth, bottom: mediaElementBottom } =
      mediaElement.getBoundingClientRect();

    const {
      width: mediaContainerWidth,

      bottom: mediaContainerBottom,
    } = mediaContainerRef.current.getBoundingClientRect();
    const mediaContainerPaddingBottom =
      mediaContainerBottom - mediaElementBottom;

    setMediaLinkStyles({
      right: (mediaContainerWidth - mediaWidth) / 2,
      bottom: mediaContainerPaddingBottom - 20,
      visibility: 'visible',
    });
  }, [props.mediaLinkText, props.mediaLinkUrl]);

  useEffect(() => {
    calculateMediaLinkStyles();
    window.addEventListener('resize', calculateMediaLinkStyles);
    return () => {
      window.removeEventListener('resize', calculateMediaLinkStyles);
    };
  }, [calculateMediaLinkStyles]);

  const renderMedia = () => {
    if (props.animationDataUrl?.endsWith('.json'))
      return (
        <LottieAnimation
          animationAssetsUrl={props.animationAssetsUrl}
          animationDataUrl={props.animationDataUrl}
          ariaLabel={props.mediaAltText}
          autoplay={true}
          className="lottie-animation"
          css={Styles.media}
          loop={props.animationShouldLoop}
          onDOMLoaded={calculateMediaLinkStyles}
          speed={props.animationSpeed}
        />
      );

    if (props.animationDataUrl)
      return (
        <div css={Styles.media}>
          <Video
            autoPlay={true}
            fullHeight={false}
            hideControls={true}
            loop={props.animationShouldLoop}
            onPlay={calculateMediaLinkStyles}
            playbackRate={props.animationSpeed}
            title={props.mediaAltText}
            videoUrl={props.animationDataUrl}
          />
        </div>
      );

    if (props.imageUrl)
      return (
        <div css={Styles.media}>
          <img
            alt={props.mediaAltText}
            onLoad={calculateMediaLinkStyles}
            src={props.imageUrl}
          />
        </div>
      );

    return null;
  };

  return (
    <section css={Styles.root(props.isReversedOnMobile)}>
      <div css={Styles.contentContainer}>
        <div css={Styles.content}>
          <div css={Styles.headingContainer}>
            {props.eyebrow && <div css={Styles.eyebrow}>{props.eyebrow}</div>}
            <Heading
              headingGraphic={props.headingGraphic}
              headingRotatingText={props.headingRotatingText}
              headingStaticText={props.headingStaticText}
            />
          </div>
          <div>
            <div css={Styles.description}>
              <RichTextHtml
                backgroundColor={COLOR_KEYS.neutral100}
                html={props.description}
              />
            </div>
            <div css={Styles.ctaContainer}>
              <CustomButton
                href={props.ctaUrl}
                isFullWidth={true}
                onClick={() =>
                  emitCTAClickEvent({
                    text: props.ctaText,
                    href: props.ctaUrl,
                    section: SectionName.SplitColorHero,
                  })
                }
              >
                {props.ctaText}
              </CustomButton>
              {props.hasGoogleSSO && (
                <Fragment>
                  <GoogleSSOButton text={props.googleSSOText} />
                </Fragment>
              )}
              {props.secondaryCtaText && props.secondaryCtaUrl && (
                <CustomButton
                  color="outline"
                  href={props.secondaryCtaUrl}
                  isFullWidth={true}
                  onClick={() =>
                    emitCTAClickEvent({
                      text: props.secondaryCtaText,
                      href: props.secondaryCtaUrl,
                      section: SectionName.SplitColorHero,
                      type: CTAType.secondary_cta_button,
                    })
                  }
                >
                  {props.secondaryCtaText}
                </CustomButton>
              )}
              {props.calloutText && (
                <div css={Styles.calloutText}>
                  <RichTextHtml
                    backgroundColor={COLOR_KEYS.neutral100}
                    html={props.calloutText}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        css={Styles.mediaContainer(
          props.backgroundColor,
          props.mediaLinkText && props.mediaLinkUrl,
          hasVideo,
        )}
        ref={mediaContainerRef}
      >
        {renderMedia()}
        {props.mediaLinkText && props.mediaLinkUrl && (
          <a
            css={Styles.mediaLink(hasVideo)}
            href={props.mediaLinkUrl}
            style={mediaLinkStyles}
          >
            {props.mediaLinkText}
            <svg
              fill="currentColor"
              viewBox="0 0 14 14"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M10.586 2H1V0h13v13h-2V3.414L1.707 13.707.293 12.293 10.586 2Z" />
            </svg>
          </a>
        )}
      </div>
    </section>
  );
}
